import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { squidloansUserAPI } from '../../services/api';
import useLeadStage from '../../hooks/useLeadStage';
import { automationNodeLeadStageToRouteMap, LEAD_STAGE } from '../../Utils/constants';
import FlowFooter from '../../components/FlowFooter';
import { formateDate } from '../../Utils/common';
import { CONTACT_NUMBER } from '../../Utils/constant';

function EsignAutomation() {
  const [loanDetails, setLoanDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [signUrl, setSignUrl] = useState('');
  const [previewError, setPreviewError] = useState('');
  const [showExpiryMessage, setShowExpiryMessage] = useState(false);
  const [bankAccountConfirmed, setBankAccountConfirmed] = useState(false);
  const navigate = useNavigate();
  const { leadStage, isLoadingStage } = useLeadStage();
  const curr_page_lead_stage = [
    LEAD_STAGE.REFERENCE_ADDED,
    LEAD_STAGE.BRE_REJECT
  ];

  useEffect(() => {
    if (isLoadingStage) {
      return;
    }

    if (leadStage && !curr_page_lead_stage.includes(leadStage)) {
      navigate(automationNodeLeadStageToRouteMap[leadStage]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadStage, navigate, isLoadingStage]);

  useEffect(() => {
    const fetchLoanDetails = async () => {
      try {
        const response = await squidloansUserAPI.getESignData();
        setLoanDetails(response.data);
      } catch (err) {
        setPreviewError('Failed to fetch loan details');
      } finally {
        setLoading(false);
      }
    };
    fetchLoanDetails();
  }, []);

  const handlePreviewClick = async () => {
    setPreviewError('');
    if (loanDetails?.masked_bank_no && !bankAccountConfirmed) {
      setPreviewError('Please accept that the bank account number you provided is correct to proceed.');
      return;
    }
    setIsPreviewLoading(true);

    try {
      const response = await squidloansUserAPI.previewSanction();

      if (
        response.data &&
        response.data.message &&
        response.data.message.includes('Sanction details are older than 1 day')
      ) {
        setShowExpiryMessage(true);
        return;
      }

      console.log("1");
      console.log("Response data:", response);

      if (response.data && response.data?.signUrl) {
        console.log("sign url found");
        setSignUrl(response.data?.signUrl);
        window.location.href = response.data.signUrl;
      } else if (response.data && response.data?.message) {
        console.log("message found");
        setPreviewError(response.data?.message);
      } else {
        setPreviewError(response.data?.message || 'No SignUrl found in response');
      }

      console.log("2");
    } catch (error) {
      console.log("Catch block");
      setPreviewError(
        error.response?.data?.message
        || 
        error.response?.message 
        ||
          'Failed to preview sanction letter. Please try again.'
      );
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleRedirectToLoanAmount = () => {
    navigate('/apply/loan-amount');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#FFFFFF] to-[#F1F5F9] text-[#1E293B] relative flex flex-col">
        <div className="relative flex-1 flex items-center justify-center px-4 pt-20 sm:pt-24 md:pt-28">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl sm:rounded-3xl p-6 sm:p-8 max-w-md w-full text-center shadow-xl">
            <div className="h-10 w-10 border-2 border-[#FF6B35]/30 border-t-[#FF6B35] rounded-full animate-spin mx-auto mb-4" />
            <h2 className="text-base sm:text-lg font-semibold mb-2 text-[#1E3A8A]">
              Loading your loan details...
            </h2>
            <p className="text-xs sm:text-sm text-[#64748B]">
              Please wait while we fetch your sanction letter.
            </p>
          </div>
        </div>
        <FlowFooter />
      </div>
    );
  }

  if (!loanDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#FFFFFF] to-[#F1F5F9] text-[#1E293B] flex flex-col">
        <div className="flex-1 flex items-center justify-center pt-20 sm:pt-24 md:pt-28">
          <p className="text-sm sm:text-base text-[#64748B]">Unable to load loan details.</p>
        </div>
        <FlowFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#FFFFFF] to-[#F1F5F9] text-[#1E293B] relative flex flex-col">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="hidden sm:block absolute -top-20 -right-10 w-80 h-80 bg-[#FF6B35]/10 blur-[120px]"></div>
        <div className="hidden sm:block absolute top-32 -left-10 w-72 h-72 bg-[#1E3A8A]/10 blur-[110px]"></div>
        <div className="hidden sm:block absolute bottom-0 right-1/3 w-96 h-96 bg-[#FF6B35]/5 blur-[130px]"></div>
      </div>

      <div className="relative flex-1 flex flex-col pt-20 sm:pt-24 md:pt-28 pb-6 sm:pb-10 px-3 sm:px-4 md:px-6 lg:px-10 mt-0 overflow-y-auto">
        <div className="max-w-3xl w-full mx-auto space-y-4 sm:space-y-6 py-2 sm:py-4">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-2xl shadow-[#FF6B35]/10">
            <h2 className="text-lg sm:text-xl font-bold mb-4 text-[#1E3A8A]">Loan Summary</h2>
            <div className="grid grid-cols-2 gap-3 text-xs sm:text-sm">
              <div className="bg-[#F8FAFC] rounded-xl p-3 border border-[#E2E8F0]">
                <p className="text-[#64748B] mb-1">Loan Amount</p>
                <p className="text-base sm:text-lg font-semibold text-[#1E3A8A]">
                  ₹{Number(loanDetails.loan_amount).toLocaleString()}
                </p>
              </div>
              <div className="bg-[#F8FAFC] rounded-xl p-3 border border-[#E2E8F0]">
                <p className="text-[#64748B] mb-1">Tenure</p>
                <p className="text-base sm:text-lg font-semibold text-[#1E3A8A]">
                  {loanDetails.tenure} days
                </p>
              </div>
              <div className="bg-[#F8FAFC] rounded-xl p-3 border border-[#E2E8F0]">
                <p className="text-[#64748B] mb-1">Disbursal Date</p>
                <p className="text-base sm:text-lg font-semibold text-[#1E3A8A]">
                  {formateDate(loanDetails.disbursal_date)}
                </p>
              </div>
              <div className="bg-[#F8FAFC] rounded-xl p-3 border border-[#E2E8F0]">
                <p className="text-[#64748B] mb-1">Processing Fee @ {loanDetails.pf_percent}%</p>
                <p className="text-base sm:text-lg font-semibold text-[#1E3A8A]">
                  ₹{Number(loanDetails.pf_amount).toLocaleString()}
                </p>
              </div>
              <div className="bg-[#F8FAFC] rounded-xl p-3 border border-[#E2E8F0]">
                <p className="text-[#64748B] mb-1">Interest Amount</p>
                <p className="text-base sm:text-lg font-semibold text-[#1E3A8A]">
                  ₹{Number(loanDetails.interest).toLocaleString()}
                </p>
              </div>
              <div className="bg-[#F8FAFC] rounded-xl p-3 border border-[#E2E8F0]">
                <p className="text-[#64748B] mb-1">Rate of Interest</p>
                <p className="text-base sm:text-lg font-semibold text-[#1E3A8A]">
                  {loanDetails.roi}% 
                </p>
              </div>
              <div className="bg-[#F8FAFC] rounded-xl p-3 border border-[#E2E8F0]">
                <p className="text-[#64748B] mb-1">Repayment</p>
                <p className="text-base sm:text-lg font-semibold text-[#1E3A8A]">
                  ₹{Number(loanDetails.repayment_amount).toLocaleString()}
                </p>
              </div>
              <div className="bg-[#F8FAFC] rounded-xl p-3 border border-[#E2E8F0]">
                <p className="text-[#64748B] mb-1">Repayment Date</p>
                <p className="text-base sm:text-lg font-semibold text-[#1E3A8A]">
                  {formateDate(loanDetails.repayment_date)}
                </p>
              </div>
            </div>

            {loanDetails.masked_bank_no && (
              <>
                <div className="mt-4 sm:mt-5 p-4 sm:p-5 rounded-xl sm:rounded-2xl border-2 border-[#FF6B35] bg-gradient-to-br from-amber-50 to-orange-50">
                  <p className="text-xs sm:text-sm font-semibold text-[#1E3A8A] mb-1">Disbursement Bank Account</p>
                  <p className="text-lg sm:text-xl font-bold text-[#1E3A8A] tracking-wider font-mono break-all">
                    {loanDetails.masked_bank_no}
                  </p>
                  <label className="mt-3 sm:mt-4 flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={bankAccountConfirmed}
                      onChange={(e) => {
                        setBankAccountConfirmed(e.target.checked);
                        if (e.target.checked) setPreviewError('');
                      }}
                      className="mt-1 h-4 w-4 rounded border-[#E2E8F0] text-[#FF6B35] focus:ring-[#FF6B35]"
                    />
                    <span className="text-xs sm:text-sm text-[#1E293B]">
                      I confirm that the bank account number shown above is correct
                    </span>
                  </label>
                  <p className="text-xs sm:text-sm text-[#64748B] mt-2">
                    If this is not your bank account, please contact our executive immediately at{' '}
                    <a
                      href={`tel:${CONTACT_NUMBER}`}
                      className="font-semibold text-[#FF6B35] hover:underline"
                    >
                      {CONTACT_NUMBER}
                    </a>
                  </p>
                </div>
                {previewError && (
                  <div className="bg-red-50 my-3 border border-red-200 rounded-xl sm:rounded-2xl p-3 sm:p-4 text-xs sm:text-sm text-red-700">
                    {previewError}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="space-y-3">
            <button
              onClick={handlePreviewClick}
              disabled={isPreviewLoading}
              className="w-full py-3 sm:py-3.5 rounded-xl sm:rounded-2xl bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] text-white text-sm sm:text-base font-semibold hover:opacity-90 transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-md hover:shadow-lg flex items-center justify-center gap-2"
            >
              {isPreviewLoading ? (
                <>
                  <span className="h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Loading...
                </>
              ) : (
                'Preview Sanction Letter'
              )}
            </button>

            {signUrl && !isPreviewLoading && (
              <a
                href={signUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center py-3 rounded-xl sm:rounded-2xl border border-[#E2E8F0] text-xs sm:text-sm text-[#1E293B] hover:bg-[#F8FAFC] transition"
              >
                Open Sanction Letter
              </a>
            )}

            {showExpiryMessage && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl sm:rounded-2xl p-3 sm:p-4 text-xs sm:text-sm text-amber-700">
                Sanction details are older than 1 day. Please reselect your loan
                amount.
                <button
                  onClick={handleRedirectToLoanAmount}
                  className="mt-2 inline-block text-xs underline text-[#FF6B35] font-semibold"
                >
                  Go to Loan Amount
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <FlowFooter />
    </div>
  );
}

export default EsignAutomation;

